import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ACTIONS_COL_WIDTH,
  Alert,
  Badge,
  Button,
  Card,
  Form,
  InputGroup,
  PageHeader,
  RowActions,
  Table,
} from '@/components/ui';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { ComplianceChecks } from '../../../components/ExpiryIndicator';
import { areasService } from '../../../services/areasService';
import {
  RESOURCE_TYPE_LABELS,
  Vendor,
  VendorQueryParams,
  vendorService,
} from '../../../services/vendorService';
import VendorsFilters, {
  EMPTY_VENDOR_FILTERS,
  VendorFilterState,
} from '../components/VendorsFilters';
import VendorModal from '../components/VendorModal';
import AddVendorToAreaModal from '../components/AddVendorToAreaModal';
import { missingCallerServiceAreaIds } from '../utils/callerAreas';

const StatusBadge: React.FC<{ status: Vendor['status'] }> = ({ status }) => {
  if (status === 'ACTIVE') return <Badge bg="success">Active</Badge>;
  if (status === 'PENDING') return <Badge bg="warning">Pending approval</Badge>;
  return <Badge bg="secondary">Inactive</Badge>;
};

/**
 * HE-M02 — one vendor row that expands to its service areas, and within each area the
 * resources and rates available.
 *
 * Hand-rolls <Table> rather than using DataTable, matching ProjectsView and ProjectsTable:
 * DataTable renders exactly one <tr> per row and has no slot for expanded content, so an
 * expandable list cannot use it. `RowActions` supplies both the expander chevron and the
 * kebab, which is what keeps this consistent with those two despite the hand-rolled table.
 *
 * The cost is that this list scrolls horizontally on a phone instead of collapsing to cards.
 * That matches WorkIds/Projects/Vehicles, which all scroll too - DataTable's card view is the
 * exception among list screens, not the rule.
 *
 * Tiger Calcium, with three service areas, is the worked example the spec names.
 */
const VendorsPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [showModal, setShowModal] = useState(false);

  const [filters, setFilters] = useState<VendorFilterState>(EMPTY_VENDOR_FILTERS);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  const [pagination, setPagination] = useState({ page: 1, limit: 20, totalCount: 0, totalPages: 0 });

  const canCreate = hasPermission('heMaster:vendor_create');
  const canAddArea =
    hasPermission('heMaster:vendor_add_area') || hasPermission('heMaster:vendor_edit');
  const canSeeDirectory = hasPermission('heMaster:vendor_directory');

  const [callerAreas, setCallerAreas] = useState<Array<{ id: number; name: string }>>([]);
  const [addTarget, setAddTarget] = useState<Vendor | null>(null);

  useEffect(() => {
    areasService
      .getServiceAreas()
      .then((areas) => setCallerAreas((areas ?? []).map((a) => ({ id: a.id, name: a.name }))))
      .catch(() => setCallerAreas([]));
  }, []);

  const callerAreaIds = callerAreas.map((a) => a.id);

  const fetchVendors = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        setError(null);
        const params: VendorQueryParams = {
          page,
          limit: pagination.limit,
          ...(searchTerm && { search: searchTerm }),
          ...(filters.statuses.length && {
            statuses: filters.statuses as VendorQueryParams['statuses'],
          }),
          ...(filters.serviceAreaIds.length && { serviceAreaIds: filters.serviceAreaIds }),
          ...(filters.complianceIssuesOnly && { complianceIssuesOnly: true }),
        };
        const response = await vendorService.getVendorsPaginated(params);
        setVendors(response.data);
        setPagination((prev) => ({
          ...prev,
          page: response.pagination.page,
          totalCount: response.pagination.totalCount,
          totalPages: response.pagination.totalPages,
        }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load vendors');
        setVendors([]);
        setPagination((prev) => ({ ...prev, totalCount: 0, totalPages: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [pagination.limit, searchTerm, filters]
  );

  useEffect(() => {
    fetchVendors(1);
  }, [searchTerm, filters, pagination.limit]);

  const toggleExpanded = (vendorId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });
  };

  const toggleSearch = () => {
    if (isSearchVisible) {
      setSearchTerm('');
      setSearchInput('');
      setIsSearchVisible(false);
      return;
    }
    // The revealed input carries autoFocus, so the caret lands there without a second click.
    setIsSearchVisible(true);
  };

  const handleCreate = async (data: {
    name: string;
    legalName?: string;
    contactEmail?: string;
    phoneNumber?: string;
    comments?: string;
    serviceAreaIds: number[];
  }) => {
    await vendorService.createVendor(data);
    setShowModal(false);
    await fetchVendors(1);
  };

  return (
    <div className="vendors-page tw-p-4">
      <PageHeader
        title="Vendors"
        actions={[
          {
            key: 'search',
            label: `Search${searchTerm ? ` (${searchTerm.length > 20 ? `${searchTerm.substring(0, 20)}…` : searchTerm})` : ''}`,
            icon: isSearchVisible ? 'bi-x-lg' : 'bi-search',
            intent: 'secondary',
            active: isSearchVisible || !!searchTerm,
            onClick: toggleSearch,
          },
          ...(canCreate
            ? [
                {
                  key: 'add',
                  label: 'Add Vendor',
                  icon: 'bi-plus-lg',
                  intent: 'primary' as const,
                  onClick: () => setShowModal(true),
                },
              ]
            : []),
        ]}
      />

      {canSeeDirectory && (
        <Alert variant="info" className="tw-mb-3">
          Vendors from every service area are listed here so you can check whether a
          subcontractor is already set up elsewhere before creating a duplicate. You can
          add an existing vendor to your area; you cannot create one outside it.
        </Alert>
      )}

      {isSearchVisible && (
        <Card className="tw-mb-3">
          <Card.Body>
            <InputGroup>
              <Form.Control
                // The input only mounts when the panel opens, so autoFocus fires exactly
                // once per open. Form.Control does not forward refs, so this is the way to
                // land the caret without a second click (matching /work-ids).
                autoFocus
                type="text"
                placeholder="Search by name, legal name, email, or phone…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && setSearchTerm(searchInput.trim())}
              />
              <Button variant="primary" type="button" onClick={() => setSearchTerm(searchInput.trim())}>
                <i className="bi bi-search tw-mr-2" />
                Search
              </Button>
              {searchTerm && (
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => {
                    setSearchInput('');
                    setSearchTerm('');
                  }}
                >
                  Clear
                </Button>
              )}
            </InputGroup>
          </Card.Body>
        </Card>
      )}

      <VendorsFilters
        filters={filters}
        onFilterChange={setFilters}
        onClearFilters={() => setFilters(EMPTY_VENDOR_FILTERS)}
      />

      {error && (
        <div className="tw-mb-3 tw-text-danger" role="alert">
          {error}
        </div>
      )}

      <Card>
        <Card.Body className="tw-p-0">
          <Table responsive hover className="tw-mb-0">
            <thead>
              <tr>
                <th>Vendor</th>
                <th>Status</th>
                <th>Service Areas</th>
                <th>Resources</th>
                <th>Compliance</th>
                <th style={{ width: ACTIONS_COL_WIDTH }} className="tw-text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="tw-text-center tw-py-4">
                    Loading vendors…
                  </td>
                </tr>
              )}

              {!loading && vendors.length === 0 && (
                <tr>
                  <td colSpan={6} className="tw-text-center tw-py-4 tw-text-content-muted">
                    No vendors match these filters.
                  </td>
                </tr>
              )}

              {!loading &&
                vendors.map((vendor) => {
                  const isOpen = expanded.has(vendor.id);
                  return (
                    <React.Fragment key={vendor.id}>
                      {/*
                        Whole row opens the vendor - the same convention DataTable and the
                        assets table use. The kebab keeps its explicit "Open Vendor" action,
                        which is also the keyboard path in, since a <tr> takes no focus.
                      */}
                      <tr
                        onClick={() => navigate(`/vendors/${vendor.id}`)}
                        className="tw-cursor-pointer"
                        title={`Open ${vendor.name}`}
                      >
                        <td>
                          <div className="tw-font-medium">{vendor.name}</div>
                          {vendor.legalName && (
                            <div className="tw-text-sm tw-text-content-muted">{vendor.legalName}</div>
                          )}
                        </td>
                        <td>
                          <StatusBadge status={vendor.status} />
                        </td>
                        <td>
                          {vendor.serviceAreas?.length
                            ? vendor.serviceAreas
                                .map((sa) => sa.serviceArea.name)
                                .join(', ')
                            : '—'}
                        </td>
                        <td>{vendor._count?.resources ?? 0}</td>
                        <td>
                          <ComplianceChecks compliance={vendor.compliance} />
                        </td>
                        <td className="tw-text-right" onClick={(e) => e.stopPropagation()}>
                          {/* Expander + three-dot kebab — the Quantified Tasks layout. */}
                          <RowActions
                            expander={{
                              expanded: isOpen,
                              onToggle: () => toggleExpanded(vendor.id),
                              title: isOpen
                                ? `Hide ${vendor.name}'s service areas`
                                : `Show ${vendor.name}'s service areas`,
                            }}
                            actions={[
                              {
                                kind: 'view',
                                label: 'Open Vendor',
                                onClick: () => navigate(`/vendors/${vendor.id}`),
                              },
                              {
                                kind: 'custom',
                                icon: 'bi-geo-alt',
                                label: 'Add to my area',
                                onClick: () => setAddTarget(vendor),
                                hidden:
                                  !canAddArea ||
                                  missingCallerServiceAreaIds(vendor, callerAreaIds).length === 0,
                              },
                            ]}
                          />
                        </td>
                      </tr>

                      {isOpen && (
                        <tr>
                          <td colSpan={6} className="tw-bg-surface-alt tw-p-0">
                            <div className="tw-p-3">
                              {vendor.serviceAreas?.length === 0 && (
                                <p className="tw-text-content-muted tw-mb-0">
                                  This vendor has no service areas.
                                </p>
                              )}
                              {vendor.serviceAreas?.map((link) => {
                                // Resources are only loaded on the detail endpoint; the list
                                // shows the area breakdown and links onward rather than
                                // fetching every vendor's whole catalogue up front.
                                const areaResources = (vendor.resources ?? []).filter((r) =>
                                  r.serviceAreas?.some((sa) => sa.serviceAreaId === link.serviceAreaId)
                                );
                                return (
                                  <div key={link.id} className="tw-mb-3 last:tw-mb-0">
                                    <div className="tw-font-medium tw-mb-1">
                                      <i className="bi bi-geo-alt tw-mr-1" />
                                      {link.serviceArea.name}
                                      {link.serviceArea.code ? ` (${link.serviceArea.code})` : ''}
                                    </div>
                                    {areaResources.length === 0 ? (
                                      <div className="tw-text-sm tw-text-content-muted tw-pl-4">
                                        Open the vendor to see and manage resources and rates in this
                                        area.
                                      </div>
                                    ) : (
                                      <ul className="tw-text-sm tw-pl-5 tw-mb-0">
                                        {areaResources.map((r) => (
                                          <li key={r.id}>
                                            {r.name}{' '}
                                            <span className="tw-text-content-muted">
                                              ({RESOURCE_TYPE_LABELS[r.resourceType]})
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {pagination.totalPages > 1 && (
        <div className="tw-mt-3">
          <ServerPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.limit}
            onPageChange={(page) => fetchVendors(page)}
            onPageSizeChange={(size) => setPagination((prev) => ({ ...prev, limit: size, page: 1 }))}
            loading={loading}
          />
        </div>
      )}

      <VendorModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onSave={handleCreate}
        callerAreas={callerAreas}
        canAddArea={canAddArea}
        onAdoptVendor={(existing) => {
          setShowModal(false);
          setAddTarget(existing);
        }}
      />

      <AddVendorToAreaModal
        show={!!addTarget}
        vendor={addTarget}
        callerAreas={callerAreas}
        onHide={() => setAddTarget(null)}
        onAdded={async () => {
          setAddTarget(null);
          await fetchVendors(pagination.page);
        }}
      />
    </div>
  );
};

export default VendorsPage;
